import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { Project, TicketSummary } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card, StatusBadge, TypeBadge } from './Card.tsx';
import { Select } from './Select.tsx';

interface Props {
  project: Project;
  tickets: TicketSummary[];
  onChange: () => void | Promise<void>;
}

// The project's tickets, with the three ways to change them: start a new
// ticket inside the project, pull in one of the client's open tickets, and
// take a ticket back out. Every move writes a private note on the ticket
// (server side), because it changes which budget the ticket's time counts
// against.
export function ProjectTicketsPanel({ project, tickets, onChange }: Props): React.ReactElement {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<TicketSummary[] | null>(null);
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The client's open tickets that are not already here. Loaded once the
  // picker is opened, so the page does not pay for it on every visit.
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!adding) return;
    let cancelled = false;
    apiFetch<{ tickets: TicketSummary[] }>(
      `/api/tickets?organizationId=${project.organizationId}&closed=false&limit=500`,
    )
      .then((r) => {
        if (!cancelled) setCandidates(r.tickets.filter((t) => t.projectId !== project.id));
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Could not load tickets.');
      });
    return () => {
      cancelled = true;
    };
  }, [adding, project.id, project.organizationId]);

  async function addTicket() {
    if (!picked) return;
    const ticket = candidates?.find((t) => t.id === picked);
    if (ticket?.projectId && !window.confirm(`T-${ticket.displayNumber} is in another project. Move it here?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/projects/${project.id}/tickets`, { method: 'POST', json: { ticketId: picked } });
      setPicked('');
      setAdding(false);
      setCandidates(null);
      await onChange();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add the ticket.');
    } finally {
      setBusy(false);
    }
  }

  async function removeTicket(t: TicketSummary) {
    if (!window.confirm(`Take T-${t.displayNumber} out of this project? Its time will no longer count here.`)) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/projects/${project.id}/tickets/${t.id}`, { method: 'DELETE' });
      await onChange();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not remove the ticket.');
    } finally {
      setBusy(false);
    }
  }

  // A client filed a simple task as a project ticket. The server makes it an
  // escalation and deletes this project only if it is now an empty
  // placeholder; then there is no project page to come back to.
  async function notAProject(t: TicketSummary) {
    if (
      !window.confirm(
        `Change T-${t.displayNumber} to an Escalation and take it out of this project? If this project was made just for that ticket and has nothing else in it, the project is deleted.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ projectDeleted: boolean }>(`/api/tickets/${t.id}/not-a-project`, {
        method: 'POST',
      });
      if (res.projectDeleted) {
        navigate('/projects');
        return;
      }
      await onChange();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not change the ticket.');
    } finally {
      setBusy(false);
    }
  }

  const newTicketUrl = `/tickets/new?organizationId=${project.organizationId}&projectId=${project.id}`;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-[var(--color-header)]">Tickets in this project</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setAdding((v) => !v)} disabled={busy}>
            {adding ? 'Cancel' : 'Add existing ticket'}
          </Button>
          <Link
            to={newTicketUrl}
            className="inline-flex items-center rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            + New ticket
          </Link>
        </div>
      </div>

      {adding && (
        <Card className="mb-3">
          {candidates === null ? (
            <p className="text-sm text-[var(--color-muted)]">Loading {project.organizationName}'s open tickets…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">
              {project.organizationName} has no other open tickets.
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[280px] flex-1">
                <Select label="Ticket" value={picked} onChange={(e) => setPicked(e.target.value)}>
                  <option value="">Choose one of {project.organizationName}'s open tickets</option>
                  {candidates.map((t) => (
                    <option key={t.id} value={t.id}>
                      T-{t.displayNumber} · {t.subject}
                      {t.projectId ? ' (in another project)' : ''}
                    </option>
                  ))}
                </Select>
              </div>
              <Button onClick={() => void addTicket()} disabled={busy || !picked}>
                {busy ? 'Adding…' : 'Add to project'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {error && <p className="mb-3 text-sm text-[var(--color-danger)]">{error}</p>}

      {tickets.length === 0 ? (
        <Card>
          <p className="text-center text-[var(--color-muted)]">No tickets in this project yet.</p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3 font-mono text-xs">T-{t.displayNumber}</td>
                  <td className="px-4 py-3">
                    <TypeBadge type={t.type} />
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/tickets/${t.id}`} className="text-[var(--color-t3-blue)] hover:underline">
                      {t.subject}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={t.status}>{t.status.replace(/_/g, ' ')}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* A project ticket must stay in a project unless it was never one. */}
                    {t.type === 'project' && (
                      <button
                        type="button"
                        onClick={() => void notAProject(t)}
                        disabled={busy}
                        className="text-xs text-[var(--color-muted)] hover:text-[var(--color-danger)] hover:underline"
                      >
                        Not a project
                      </button>
                    )}
                    {t.type !== 'project' && (
                      <button
                        type="button"
                        onClick={() => void removeTicket(t)}
                        disabled={busy}
                        className="text-xs text-[var(--color-muted)] hover:text-[var(--color-danger)] hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
